import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { WorkflowConfigurationPanel } from 'components/StudyDashboard/Pipelines/Multiplex/WorkflowConfigurationPanel';
import {
  getStudyWorkflowConfiguration,
  updateStudyWorkflowConfiguration,
} from 'components/StudyDashboard/Workflow/studyWorkflowConfiguration';
import { NamedFeature } from 'interfaces/features';
import { InferredFeatureConfig } from 'interfaces/inferredFeatures';
import { Permission } from 'interfaces/permissionOption';
import { QualityControlLabel } from 'interfaces/qualityControlLabels';
import { PlatformVersionPolicy, Study } from 'interfaces/study';
import { filter, find, includes, isEmpty, map, omit } from 'lodash';
import React, { FunctionComponent } from 'react';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useCurrentUser } from 'utils/useCurrentUser';
import { usePermissions } from 'utils/usePermissions';
import StudyAcl from '../StudyAcl';
import StudyAdmin from '../StudyAdmin';
import CellRuleStudySettings from './CellRuleStudySettings';
import { DeconvolutionCalculationSettings } from './DeconvolutionSettings/DeconvolutionCalculationSettings';
import { DeconvolutionVisualizationSettings } from './DeconvolutionSettings/DeconvolutionVisualizationSettings';
import FeaturesSettings from './FeaturesSettings/FeaturesSettings';
import { GeneralSettings } from './GeneralSettings';
import InferredFeaturesSettings from './InferredFeaturesSettings/InferredFeaturesSettings';
import { PeakDetectionSettings } from './PeakDetectionSettings';
import { DEFAULT_PLATFORM_VERSION_POLICY, PlatformImageSelect, PlatformVersionPolicySelect } from './PlatformSettings';
import { PostIngestPipelineSettings } from './PostIngestPipelineSettings';
import { StudyAlgorithmsSettings } from './StudyAlgorithmsSettings';
import { StudyExternalStorageSettingsControl } from './StudyExternalStorageSettings';
import StudyLabelsSettings from './StudyLabel/StudyLabelsSettings';

interface StudySettingsProps {
  study: Study;
  localHighlightedFeatures?: NamedFeature[];
  setLocalHighlightedFeatures?: React.Dispatch<React.SetStateAction<NamedFeature[]>>;
  localHiddenFeatures?: NamedFeature[];
  setLocalHiddenFeatures?: React.Dispatch<React.SetStateAction<NamedFeature[]>>;
  localInferredFeatures?: InferredFeatureConfig[];
  setLocalInferredFeatures?: React.Dispatch<React.SetStateAction<InferredFeatureConfig[]>>;
  localLabels?: QualityControlLabel[];
  setLocalLabels?: React.Dispatch<React.SetStateAction<QualityControlLabel[]>>;
  updatedSettings?: Study['settings'];
  setUpdatedSettings?: React.Dispatch<React.SetStateAction<Study['settings']>>;
  updatedPlatformSettings?: Study['platformSettings'];
  setUpdatedPlatformSettings?: React.Dispatch<React.SetStateAction<Study['platformSettings']>>;
  localUsersAcl?: Study['usersAcl'];
  setLocalUsersAcl?: React.Dispatch<React.SetStateAction<Study['usersAcl']>>;
  localStudyAdmins?: Study['studyAdmins'];
  setLocalStudyAdmins?: React.Dispatch<React.SetStateAction<Study['studyAdmins']>>;
}

type StudySettingsTabId = 'workspace' | 'platform' | 'external-storage' | 'features' | 'labels' | 'workflow' | 'access';

interface StudySettingsTabDefinition {
  id: StudySettingsTabId;
  label: string;
  description: string;
  isVisible: boolean;
  content: React.ReactNode;
}

const StudySettings: FunctionComponent<React.PropsWithChildren<StudySettingsProps>> = ({
  study,
  localHighlightedFeatures,
  setLocalHighlightedFeatures,
  localHiddenFeatures,
  setLocalHiddenFeatures,
  localInferredFeatures,
  setLocalInferredFeatures,
  localLabels,
  setLocalLabels,
  updatedSettings,
  setUpdatedSettings,
  updatedPlatformSettings,
  setUpdatedPlatformSettings,
  localUsersAcl,
  setLocalUsersAcl,
  localStudyAdmins,
  setLocalStudyAdmins,
}) => {
  const { hasPermission } = usePermissions();
  const { userId: currentUserId } = useCurrentUser();

  const isStudyAdmin = includes(study.studyAdmins, currentUserId);

  const globalLabels = study.qcLabelsConfig;
  const globalInferredFeaturesConfig = study.inferredFeaturesConfig;

  const hasPermissionToEditQcQaLabels = hasPermission(Permission.EditQcQaLabels);
  const hasPermissionToEditInferredFeatures = hasPermission(Permission.EditInferredFeatures);
  const hasPermissionToEditPlatformSettings = hasPermission(Permission.EditPlatformSettings);
  const hasPermissionToEditCellRules = hasPermission(Permission.EditCellRules);
  const hasPermissionToEditStudyAlgorithmsSettings = hasPermission(Permission.EditStudyAlgorithms);
  const hasPermissionToEditExportBucketSettings = hasPermission(Permission.EditStudyExportBucketSettings);
  const hasPermissionToEditExternalSlideStorageSettings = hasPermission(
    Permission.EditStudyExternalSlideStorageSettings
  );
  const hasPermissionToEditStudyAcl = hasPermission(Permission.EditStudyAcl);
  const hasPermissionToEditDeconvolutionVisualizationSettings = hasPermission(
    Permission.EditDeconvolutionVisualizationSettings
  );
  const hasPermissionToEditDeconvolutionCalculationSettings = hasPermission(
    Permission.EditDeconvolutionCalculationSettings
  );

  const hasPermissionToViewStudyCellRules = hasPermission(Permission.ViewStudyCellRules);
  const hasPermissionToEditPostIngestPipeline = hasPermission(Permission.EditStudyPostIngestPipelineSettings);
  const workflowConfiguration = getStudyWorkflowConfiguration(updatedSettings?.workflow?.modules);

  const { data: labs } = useLabs({ enabled: hasPermissionToEditPlatformSettings });
  const labDefaultPlatformVersionPolicy =
    find(labs, { id: study.labId })?.defaultStudySettings?.platform?.versionPolicy ?? DEFAULT_PLATFORM_VERSION_POLICY;
  const studyPlatformVersionPolicy = updatedSettings?.platform?.versionPolicy;
  const effectivePlatformVersionPolicy = studyPlatformVersionPolicy ?? labDefaultPlatformVersionPolicy;

  const updatePlatformVersionPolicy = (versionPolicy: PlatformVersionPolicy | undefined) => {
    setUpdatedSettings((currentSettings) => {
      const nextPlatform = versionPolicy
        ? { ...currentSettings?.platform, versionPolicy }
        : omit(currentSettings?.platform, 'versionPolicy');
      // An empty object is kept when the saved study has a policy, so the save merge clears it.
      return isEmpty(nextPlatform) && !study.settings?.platform
        ? omit(currentSettings, 'platform')
        : { ...currentSettings, platform: nextPlatform };
    });
  };
  const updateWorkflowModule = (key: keyof typeof workflowConfiguration, enabled: boolean) => {
    setUpdatedSettings((currentSettings) => ({
      ...currentSettings,
      workflow: {
        ...currentSettings?.workflow,
        modules: updateStudyWorkflowConfiguration(
          getStudyWorkflowConfiguration(currentSettings?.workflow?.modules),
          key,
          enabled
        ),
      },
    }));
  };

  const tabs: StudySettingsTabDefinition[] = [
    {
      id: 'workspace',
      label: 'Workspace',
      description:
        'Choose the default viewing behavior for this study so operators land in the right stain and viewer context.',
      isVisible: true,
      content: (
        <Grid item xs={12} sx={{ px: 2 }}>
          <GeneralSettings
            studyId={study.id}
            updatedSettings={updatedSettings}
            setUpdatedSettings={setUpdatedSettings}
          />
        </Grid>
      ),
    },
    {
      id: 'platform',
      label: 'Platform',
      description:
        'Manage runtime and delivery settings for this study without mixing them with scientific review settings.',
      isVisible: hasPermissionToEditPlatformSettings,
      content: (
        <>
          {hasPermissionToEditPlatformSettings && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h6">Platform Settings</Typography>
                </Grid>
                <Grid item xs={12}>
                  <PlatformVersionPolicySelect
                    value={studyPlatformVersionPolicy}
                    onChange={updatePlatformVersionPolicy}
                    labDefaultPolicy={labDefaultPlatformVersionPolicy}
                    helperText={
                      effectivePlatformVersionPolicy === 'latest'
                        ? 'Each time a job form opens, this study moves to the latest stable Platform Version.'
                        : 'Jobs run on the Platform Version selected below.'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <PlatformImageSelect
                    updatedSettings={updatedPlatformSettings ?? study.platformSettings}
                    setUpdatedSettings={setUpdatedPlatformSettings}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      ),
    },
    {
      id: 'external-storage',
      label: 'External Storage',
      description:
        'Configure approved external locations for slide transfers and published study results. Provider credentials remain managed at the lab level.',
      isVisible: hasPermissionToEditExternalSlideStorageSettings || hasPermissionToEditExportBucketSettings,
      content: (
        <Grid item xs={12} sx={{ px: 2 }}>
          <StudyExternalStorageSettingsControl
            study={study}
            updatedSettings={updatedSettings}
            setUpdatedSettings={setUpdatedSettings}
            canEditSlideSourceBuckets={hasPermissionToEditExternalSlideStorageSettings}
            canEditResultExportBuckets={hasPermissionToEditExportBucketSettings}
          />
        </Grid>
      ),
    },
    {
      id: 'features',
      label: 'Features',
      description:
        'Curate the feature set that stays front and center in the study. Highlighted, hidden, and inferred feature controls live together here.',
      isVisible: true,
      content: (
        <>
          <Grid item xs={12} sx={{ px: 2 }}>
            <FeaturesSettings
              study={study}
              highlightedFeatures={localHighlightedFeatures}
              setHighlightedFeatures={setLocalHighlightedFeatures}
              hiddenFeatures={localHiddenFeatures}
              setHiddenFeatures={setLocalHiddenFeatures}
            />
          </Grid>
          {hasPermissionToEditInferredFeatures && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <InferredFeaturesSettings
                study={study}
                hasUnsavedInferredFeatures={Boolean(localInferredFeatures)}
                setInferredFeaturesConfig={setLocalInferredFeatures}
                inferredFeaturesConfig={localInferredFeatures ?? globalInferredFeaturesConfig}
              />
            </Grid>
          )}
        </>
      ),
    },
    {
      id: 'labels',
      label: 'Labels',
      description: 'Manage the shared label taxonomy used for QC and review workflows.',
      isVisible: hasPermissionToEditQcQaLabels,
      content: (
        <Grid item xs={12} sx={{ px: 2 }}>
          <StudyLabelsSettings
            study={study}
            hasUnsavedLabels={Boolean(localLabels)}
            setLabels={setLocalLabels}
            labels={localLabels ?? globalLabels}
          />
        </Grid>
      ),
    },
    {
      id: 'workflow',
      label: 'Workflow',
      description:
        'Configure study-level processing behavior. Template-defined algorithms, post-processing, and rule-driven outputs can grow here.',
      isVisible:
        hasPermissionToEditStudyAlgorithmsSettings ||
        hasPermissionToEditCellRules ||
        hasPermissionToViewStudyCellRules ||
        hasPermissionToEditDeconvolutionVisualizationSettings ||
        hasPermissionToEditDeconvolutionCalculationSettings ||
        hasPermissionToEditPostIngestPipeline,
      content: (
        <>
          {hasPermissionToEditPostIngestPipeline && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <PostIngestPipelineSettings updatedSettings={updatedSettings} setUpdatedSettings={setUpdatedSettings} />
            </Grid>
          )}
          <Grid item xs={12} sx={{ px: 2 }}>
            <WorkflowConfigurationPanel configuration={workflowConfiguration} onChange={updateWorkflowModule} />
          </Grid>
          <Grid item xs={12} sx={{ px: 2 }}>
            <PeakDetectionSettings updatedSettings={updatedSettings} setUpdatedSettings={setUpdatedSettings} />
          </Grid>
          {hasPermissionToEditStudyAlgorithmsSettings && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <StudyAlgorithmsSettings updatedSettings={updatedSettings} setUpdatedSettings={setUpdatedSettings} />
            </Grid>
          )}
          {(hasPermissionToEditCellRules || hasPermissionToViewStudyCellRules) && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <CellRuleStudySettings studyId={study.id} editable={hasPermissionToEditCellRules} />
            </Grid>
          )}
          {hasPermissionToEditDeconvolutionVisualizationSettings && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <DeconvolutionVisualizationSettings
                updatedSettings={updatedSettings}
                setUpdatedSettings={setUpdatedSettings}
              />
            </Grid>
          )}
          {hasPermissionToEditDeconvolutionCalculationSettings && (
            <Grid item xs={12} sx={{ px: 2 }}>
              <DeconvolutionCalculationSettings
                studyId={study.id}
                updatedSettings={updatedSettings}
                setUpdatedSettings={setUpdatedSettings}
              />
            </Grid>
          )}
        </>
      ),
    },
    {
      id: 'access',
      label: 'Access',
      description:
        'Assign the study admin and ACL for this study without mixing access decisions into technical settings.',
      isVisible: hasPermissionToEditStudyAcl || isStudyAdmin,
      content: (
        <Grid item xs={12} sx={{ px: 2 }}>
          <Grid sx={{ p: 2 }} container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Access Settings</Typography>
            </Grid>
            <Grid item xs={12}>
              <StudyAdmin
                presetStudyAdmin={(localStudyAdmins || study.studyAdmins)?.[0] || ''}
                onChange={(value) => setLocalStudyAdmins([value])}
                studyId={study.id}
              />
            </Grid>
            <Grid item xs={12}>
              <StudyAcl
                presetUsers={localUsersAcl || study.usersAcl}
                lockedUsers={localStudyAdmins || study.studyAdmins}
                onChange={setLocalUsersAcl}
                studyId={study.id}
              />
            </Grid>
          </Grid>
        </Grid>
      ),
    },
  ];

  const visibleTabs = filter(tabs, ({ isVisible }) => isVisible);
  const [activeTab, setActiveTab] = React.useState<StudySettingsTabId>(visibleTabs[0]?.id ?? 'workspace');

  if (!visibleTabs.length) {
    return null;
  }

  const selectedTab = find(visibleTabs, { id: activeTab }) || visibleTabs[0]!;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        height: {
          xs: '58vh',
          md: '64vh',
        },
        minHeight: {
          xs: 420,
          md: 520,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Tabs
          value={selectedTab.id}
          onChange={(_, nextTab) => setActiveTab(nextTab as StudySettingsTabId)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="study settings tabs"
        >
          {map(visibleTabs, (tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760, overflowWrap: 'anywhere' }}>
        {selectedTab.description}
      </Typography>

      <Box
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1,
          scrollbarGutter: 'stable',
        }}
      >
        <Grid container spacing={2} sx={{ pb: 1 }}>
          {selectedTab.content}
        </Grid>
      </Box>
    </Box>
  );
};

export default StudySettings;
